import React, { useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  CurrencyDollarIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  FunnelIcon,
  MapPinIcon,
  PencilIcon,
  TableCellsIcon,
  TrashIcon,
  ListBulletIcon,
  XMarkIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import {
  catalogueAPI,
  customerPurchaseOrdersAPI,
  productStockAPI,
  Product,
  Category,
  ProductStockRow,
  CostRange,
  DocumentType,
  API_BASE_URL,
} from '../../services/api';
import ProductDetailModal from './ProductDetailModal';
import ProductQuoteHistoryPanel from './ProductQuoteHistoryPanel';
import { Badge, Button, Card, CardBody, EmptyState, Modal, PageHero, SearchInput, Spinner } from '../ui';

const initialCostRangeForm = {
  minQuantity: '',
  maxQuantity: '',
  unit: '',
  pricePerUnit: '',
  currency: 'USD',
};

const CatalogueView: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [stockRows, setStockRows] = useState<ProductStockRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [viewMode, setViewMode] = useState<'inline' | 'grouped'>('inline');

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loadingEditProduct, setLoadingEditProduct] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCostRangeForm, setShowCostRangeForm] = useState(false);
  const [editingCostRange, setEditingCostRange] = useState<CostRange | null>(null);
  const [costRangeForm, setCostRangeForm] = useState(initialCostRangeForm);
  const [savingCostRange, setSavingCostRange] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<DocumentType>('COA');
  const [docName, setDocName] = useState('');
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [documentEditForm, setDocumentEditForm] = useState({ fileName: '', fileType: 'COA' as DocumentType });
  const [savingDocument, setSavingDocument] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, stockData] = await Promise.all([
        catalogueAPI.getProducts({
          categoryId: selectedCategory,
          search: searchTerm || undefined,
          isActive: true,
        }),
        catalogueAPI.getCategories(),
        productStockAPI.getAll({
          categoryId: selectedCategory,
          search: searchTerm || undefined,
          isActive: true,
        }),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
      setStockRows(stockData);
    } catch (error) {
      console.error('Error loading catalogue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quantityRanges = useMemo(() => {
    const ranges = new Set<string>();
    products.forEach((product) => {
      product.costRanges?.forEach((range) => {
        const unit = range.unit || product.unit || 'unit';
        const rangeKey = `${unit}|${range.minQuantity}-${range.maxQuantity || 'unlimited'}`;
        ranges.add(rangeKey);
      });
    });
    return Array.from(ranges).sort((a, b) => {
      const [aUnit, aRange] = a.split('|');
      const [bUnit, bRange] = b.split('|');
      if (aUnit !== bUnit) return aUnit.localeCompare(bUnit);
      const aMin = parseInt(aRange.split('-')[0], 10);
      const bMin = parseInt(bRange.split('-')[0], 10);
      return aMin - bMin;
    });
  }, [products]);

  const stockByProductId = useMemo(
    () => new Map(stockRows.map((row) => [row.productId, row])),
    [stockRows],
  );

  const filteredProducts = products.filter((product) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search ||
      product.name.toLowerCase().includes(search) ||
      product.sku.toLowerCase().includes(search) ||
      product.description?.toLowerCase().includes(search);

    const matchesCategory = !selectedCategory || product.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPriceForRange = (product: Product, rangeKey: string) => {
    const [unit, quantityRange] = rangeKey.split('|');
    const [minQty, maxQty] = quantityRange.split('-');
    const range = product.costRanges?.find((item) => {
      const rangeMax = item.maxQuantity || 'unlimited';
      const rangeUnit = item.unit || product.unit || 'unit';
      return rangeUnit === unit && item.minQuantity.toString() === minQty && rangeMax.toString() === maxQty;
    });
    return range ? `$${parseFloat(range.pricePerUnit).toFixed(2)}` : '-';
  };

  const formatLots = (productId: number) => {
    const lotNumbers = stockByProductId.get(productId)?.lotNumbers || [];
    if (lotNumbers.length === 0) return '-';
    if (lotNumbers.length <= 2) return lotNumbers.join(', ');
    return `${lotNumbers.slice(0, 2).join(', ')} +${lotNumbers.length - 2}`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString();
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatMoney = (amount?: number, currency = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return `${currency} ${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatLastQuotedPrice = (product: Product) => {
    const lastQuotedPrice = product.lastQuotedPrice;
    if (!lastQuotedPrice) return '-';
    return formatMoney(lastQuotedPrice.effectiveUnitPrice, lastQuotedPrice.currency);
  };

  const formatLastQuotedPriceMeta = (product: Product) => {
    const lastQuotedPrice = product.lastQuotedPrice;
    if (!lastQuotedPrice) return null;
    const date = formatDate(lastQuotedPrice.quoteCreatedAt);
    return lastQuotedPrice.client?.name
      ? `${lastQuotedPrice.client.name} - ${date}`
      : date;
  };

  const getDocumentUrl = (fileUrl: string) => {
    if (!fileUrl) return '#';
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    return `${API_BASE_URL}/${fileUrl.replace(/\\/g, '/')}`;
  };

  const resetEditForms = () => {
    setShowCostRangeForm(false);
    setEditingCostRange(null);
    setCostRangeForm(initialCostRangeForm);
    setShowUploadForm(false);
    setDocFile(null);
    setDocName('');
    setDocType('COA');
  };

  const handleViewProduct = async (product: Product) => {
    try {
      const fullProduct = await catalogueAPI.getProduct(product.id);
      setSelectedProduct(fullProduct);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading product details:', error);
      alert('Failed to load product details');
    }
  };

  const openEditProduct = async (product: Product) => {
    try {
      setLoadingEditProduct(true);
      setShowEditModal(true);
      setMessage(null);
      resetEditForms();
      const fullProduct = await catalogueAPI.getProduct(product.id);
      setEditingProduct(fullProduct);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load product editing controls' });
    } finally {
      setLoadingEditProduct(false);
    }
  };

  const refreshEditingProduct = async () => {
    if (!editingProduct) return;
    const fullProduct = await catalogueAPI.getProduct(editingProduct.id);
    setEditingProduct(fullProduct);
    await loadData();
  };

  const handleUpdateInventoryLink = async (isInventoryLinked: boolean) => {
    if (!editingProduct) return;

    try {
      const updatedProduct = await catalogueAPI.updateProductInventoryLink(editingProduct.id, isInventoryLinked);
      setEditingProduct((current) => current ? { ...current, ...updatedProduct } : updatedProduct);
      setProducts((current) =>
        current.map((product) =>
          product.id === updatedProduct.id
            ? { ...product, isInventoryLinked: updatedProduct.isInventoryLinked }
            : product,
        ),
      );
      setMessage({ type: 'success', text: `Inventory link ${isInventoryLinked ? 'enabled' : 'disabled'}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update inventory link' });
    }
  };

  const openAddCostRange = () => {
    setEditingCostRange(null);
    setCostRangeForm({ ...initialCostRangeForm, unit: editingProduct?.unit || '' });
    setShowCostRangeForm(true);
    setShowUploadForm(false);
  };

  const openEditCostRange = (range: CostRange) => {
    setEditingCostRange(range);
    setCostRangeForm({
      minQuantity: range.minQuantity.toString(),
      maxQuantity: range.maxQuantity?.toString() || '',
      unit: range.unit || editingProduct?.unit || '',
      pricePerUnit: parseFloat(range.pricePerUnit).toString(),
      currency: range.currency || 'USD',
    });
    setShowCostRangeForm(true);
    setShowUploadForm(false);
  };

  const handleSaveCostRange = async () => {
    if (!editingProduct || !costRangeForm.minQuantity || !costRangeForm.pricePerUnit) {
      setMessage({ type: 'error', text: 'Minimum quantity and price per unit are required' });
      return;
    }

    try {
      setSavingCostRange(true);
      const payload: any = {
        minQuantity: parseInt(costRangeForm.minQuantity, 10),
        maxQuantity: costRangeForm.maxQuantity ? parseInt(costRangeForm.maxQuantity, 10) : null,
        unit: costRangeForm.unit || editingProduct.unit || undefined,
        pricePerUnit: parseFloat(costRangeForm.pricePerUnit),
        currency: costRangeForm.currency,
      };

      if (editingCostRange) {
        await catalogueAPI.updateCostRange(editingCostRange.id, payload);
        setMessage({ type: 'success', text: 'Price range updated' });
      } else {
        await catalogueAPI.createCostRange(editingProduct.id, payload);
        setMessage({ type: 'success', text: 'Price range added' });
      }

      setShowCostRangeForm(false);
      setEditingCostRange(null);
      setCostRangeForm(initialCostRangeForm);
      await refreshEditingProduct();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save price range' });
    } finally {
      setSavingCostRange(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!editingProduct || !docFile) {
      setMessage({ type: 'error', text: 'Choose a document file first' });
      return;
    }

    try {
      setUploadingDoc(true);
      await catalogueAPI.uploadDocument(editingProduct.id, docFile, docType, docName.trim() || undefined);
      setMessage({ type: 'success', text: 'Document uploaded' });
      setShowUploadForm(false);
      setDocFile(null);
      setDocName('');
      setDocType('COA');
      await refreshEditingProduct();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to upload document' });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!editingProduct) return;

    try {
      await catalogueAPI.deleteDocument(documentId);
      setMessage({ type: 'success', text: 'Document deleted' });
      await refreshEditingProduct();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete document' });
    }
  };

  const beginEditDocument = (document: NonNullable<Product['documents']>[number]) => {
    setEditingDocumentId(document.id);
    setDocumentEditForm({
      fileName: document.fileName || document.originalName,
      fileType: document.fileType,
    });
  };

  const handleUpdateDocument = async () => {
    if (!editingDocumentId) return;

    try {
      setSavingDocument(true);
      await catalogueAPI.updateDocument(editingDocumentId, {
        fileName: documentEditForm.fileName.trim(),
        fileType: documentEditForm.fileType,
      });
      setMessage({ type: 'success', text: 'Document updated' });
      setEditingDocumentId(null);
      await refreshEditingProduct();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update document' });
    } finally {
      setSavingDocument(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8" data-tour="page-content">
      <PageHero
        title="Product Catalogue"
        subtitle="Browse products, pricing, stock, and catalogue controls"
        icon={<BookOpenIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="purple"
      />

      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search products..."
          />
        </div>

        <div className="sm:w-64">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <FunnelIcon className="h-5 w-5 text-neutral-400" />
            </div>
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : undefined)}
              className="block w-full rounded-lg border border-neutral-300 py-2 pl-10 pr-10 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="inline-flex rounded-lg shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setViewMode('inline')}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${viewMode === 'inline'
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
              }`}
            aria-label="Show inline price ranges"
          >
            <ListBulletIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('grouped')}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border-t border-r border-b ${viewMode === 'grouped'
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
              }`}
            aria-label="Show grouped price columns"
          >
            <TableCellsIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-2 text-sm text-neutral-500">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <EmptyState
                title="No products found"
                description="Try adjusting your search or filter criteria."
              />
            ) : (
              <>
                <div className="space-y-3 md:hidden">
                  {filteredProducts.map((product) => (
                    <Card key={product.id} variant="elevated">
                      <CardBody className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-neutral-900">{product.name}</p>
                            <p className="mt-1 text-xs text-neutral-500">{product.sku}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleViewProduct(product)}
                              className="rounded-md p-1.5 text-primary-600 transition-colors hover:bg-primary-50"
                              aria-label={`View product ${product.name}`}
                              title="View product"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditProduct(product)}
                              className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-neutral-100"
                              aria-label={`Edit catalogue controls for ${product.name}`}
                              title="Edit catalogue controls"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs font-medium uppercase text-neutral-500">Last Quote</p>
                            <p className="mt-1 font-semibold text-neutral-900">{formatLastQuotedPrice(product)}</p>
                            {product.lastQuotedPrice && (
                              <p className="mt-1 text-xs text-neutral-500">
                                {formatLastQuotedPriceMeta(product)}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-neutral-500">Lots</p>
                            <p className="mt-1 text-neutral-700">{formatLots(product.id)}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-neutral-500">Category</p>
                            <p className="mt-1 text-neutral-700">{product.category?.name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-neutral-500">Unit</p>
                            <p className="mt-1 text-neutral-700">{product.unit || '-'}</p>
                          </div>
                        </div>
                        <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                          <p className="text-xs font-medium uppercase text-neutral-500">Pricing</p>
                          {product.costRanges && product.costRanges.length > 0 ? (
                            <div className="mt-2 space-y-1">
                              {product.costRanges.map((range) => (
                                <div key={range.id} className="flex items-center justify-between text-xs">
                                  <span>{range.unit || product.unit || 'unit'} {range.minQuantity}-{range.maxQuantity || 'unlimited'}</span>
                                  <span className="font-semibold text-neutral-900">
                                    {formatMoney(parseFloat(range.pricePerUnit), range.currency)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-xs text-neutral-500">No pricing set yet.</p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>

                <Card variant="elevated" className="hidden md:block">
                <CardBody className="p-0">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 sm:pl-6">SKU</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Product Name</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Category</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Unit</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Lot Numbers</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Last Quote</th>
                        {viewMode === 'inline' ? (
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">Cost Ranges</th>
                        ) : (
                          quantityRanges.map((range) => {
                            const [unit, quantityRange] = range.split('|');
                            const [min, max] = quantityRange.split('-');
                            return (
                              <th key={range} className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900">
                                {unit} {min}-{max === 'unlimited' ? 'unlimited' : max}
                              </th>
                            );
                          })
                        )}
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white">
                      {filteredProducts.map((product) => (
                        <tr key={product.id} className="hover:bg-neutral-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-neutral-900 sm:pl-6">{product.sku}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-900">{product.name}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">{product.category?.name || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">{product.unit || '-'}</td>
                          <td className="max-w-xs px-3 py-4 text-sm text-neutral-500">{formatLots(product.id)}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-700">
                            {product.lastQuotedPrice ? (
                              <div>
                                <div className="font-semibold text-neutral-900">{formatLastQuotedPrice(product)}</div>
                                <div className="text-xs text-neutral-500">
                                  {formatLastQuotedPriceMeta(product)}
                                </div>
                              </div>
                            ) : '-'}
                          </td>
                          {viewMode === 'inline' ? (
                            <td className="px-3 py-4 text-sm text-neutral-500">
                              {product.costRanges && product.costRanges.length > 0 ? (
                                <div className="space-y-1">
                                  {product.costRanges.map((range) => (
                                    <div key={range.id} className="text-xs">
                                      {range.unit || product.unit || 'unit'} {range.minQuantity}-{range.maxQuantity || 'unlimited'}: ${parseFloat(range.pricePerUnit).toFixed(2)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          ) : (
                            quantityRanges.map((range) => (
                              <td key={range} className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                                {getPriceForRange(product, range)}
                              </td>
                            ))
                          )}
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleViewProduct(product)}
                                className="rounded-md p-1.5 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-900"
                                aria-label={`View product ${product.name}`}
                                title="View product"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openEditProduct(product)}
                                className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
                                aria-label={`Edit catalogue controls for ${product.name}`}
                                title="Edit catalogue controls"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
              </>
            )}
          </div>
        </div>
      </div>

      {showDetailModal && selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProduct(null);
          }}
          readOnly={true}
          allowDocumentManagement={true}
        />
      )}

      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingProduct(null);
          resetEditForms();
        }}
        title={editingProduct ? `Catalogue Controls: ${editingProduct.name}` : 'Catalogue Controls'}
        subtitle="Edit pricing, documents, inventory linking, and review stock history."
        size="full"
      >
        {loadingEditProduct || !editingProduct ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-6">
            {message && (
              <div className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium ${message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {message.type === 'success' ? <CheckIcon className="h-5 w-5" /> : <XMarkIcon className="h-5 w-5" />}
                {message.text}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-2 xl:grid-cols-5">
              <div>
                <p className="text-xs font-medium uppercase text-neutral-500">SKU</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900">{editingProduct.sku}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-neutral-500">Category</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900">{editingProduct.category?.name || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-neutral-500">Unit</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900">{editingProduct.unit || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-neutral-500">Inventory Link</p>
                <label className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <input
                    type="checkbox"
                    checked={editingProduct.isInventoryLinked !== false}
                    onChange={(event) => handleUpdateInventoryLink(event.target.checked)}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  {editingProduct.isInventoryLinked === false ? 'Not linked' : 'Linked'}
                </label>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-neutral-500">Last Quote</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900">{formatLastQuotedPrice(editingProduct)}</p>
                {editingProduct.lastQuotedPrice && (
                  <p className="mt-1 text-xs text-neutral-500">
                    {formatLastQuotedPriceMeta(editingProduct)}
                  </p>
                )}
              </div>
            </div>

            <section className="rounded-lg border border-neutral-200 bg-white p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <ArchiveBoxIcon className="h-5 w-5 text-primary-500" />
                  Lots & Stock
                </h3>
                <Badge variant={editingProduct.stockSummary?.isInventoryLinked === false ? 'neutral' : 'primary'} size="sm">
                  {editingProduct.stockSummary?.isInventoryLinked === false
                    ? 'Not tracked'
                    : `${editingProduct.stockSummary?.availableQuantity ?? 0} available`}
                </Badge>
              </div>
              {editingProduct.lotSummaries && editingProduct.lotSummaries.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Lot</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Available</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Expiry</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {editingProduct.lotSummaries.map((lot) => (
                        <tr key={`${lot.lotId || lot.lotNumber}`}>
                          <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                            {lot.lotNumber}
                            {lot.supplier?.name && <div className="mt-1 text-xs text-neutral-500">{lot.supplier.name}</div>}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{lot.availableQuantity} / {lot.totalQuantity}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{formatDate(lot.expiryDate)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">
                            <span className="inline-flex items-center gap-1">
                              <MapPinIcon className="h-4 w-4 text-neutral-400" />
                              {lot.locations?.join(', ') || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No lot-level inventory records for this product.</p>
              )}
            </section>

            <section className="rounded-lg border border-neutral-200 bg-white p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <CurrencyDollarIcon className="h-5 w-5 text-primary-500" />
                  Pricing
                </h3>
                <Button variant="primary" size="sm" onClick={openAddCostRange} leftIcon={<CurrencyDollarIcon className="h-4 w-4" />}>
                  Add Price
                </Button>
              </div>

              {editingProduct.costRanges && editingProduct.costRanges.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Min</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Max</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Unit</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Price</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-neutral-500">Currency</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase text-neutral-500">Edit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {editingProduct.costRanges.map((range) => (
                        <tr key={range.id}>
                          <td className="px-4 py-3 text-sm text-neutral-700">{range.minQuantity}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{range.maxQuantity || 'unlimited'}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{range.unit || editingProduct.unit || '-'}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-neutral-900">${parseFloat(range.pricePerUnit).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{range.currency}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => openEditCostRange(range)}
                              className="rounded-md p-1.5 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-800"
                              title="Edit price"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No pricing set yet.</p>
              )}

              {showCostRangeForm && (
                <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-neutral-900">
                    {editingCostRange ? 'Edit Price Range' : 'Add Price Range'}
                  </h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Min Quantity *</label>
                      <input
                        type="number"
                        value={costRangeForm.minQuantity}
                        onChange={(event) => setCostRangeForm({ ...costRangeForm, minQuantity: event.target.value })}
                        min="1"
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Max Quantity</label>
                      <input
                        type="number"
                        value={costRangeForm.maxQuantity}
                        onChange={(event) => setCostRangeForm({ ...costRangeForm, maxQuantity: event.target.value })}
                        min="1"
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Unit</label>
                      <input
                        type="text"
                        value={costRangeForm.unit}
                        onChange={(event) => setCostRangeForm({ ...costRangeForm, unit: event.target.value })}
                        placeholder={editingProduct.unit || 'unit'}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Price per Unit *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={costRangeForm.pricePerUnit}
                        onChange={(event) => setCostRangeForm({ ...costRangeForm, pricePerUnit: event.target.value })}
                        min="0"
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Currency</label>
                      <select
                        value={costRangeForm.currency}
                        onChange={(event) => setCostRangeForm({ ...costRangeForm, currency: event.target.value })}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="AED">AED</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button variant="primary" size="sm" onClick={handleSaveCostRange} isLoading={savingCostRange}>
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setShowCostRangeForm(false);
                      setEditingCostRange(null);
                      setCostRangeForm(initialCostRangeForm);
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>

            <ProductQuoteHistoryPanel productId={editingProduct.id} />

            <section className="rounded-lg border border-neutral-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <DocumentTextIcon className="h-5 w-5 text-primary-500" />
                  Customer Purchase Orders
                </h3>
                <Badge variant="neutral" size="sm">{editingProduct.customerPurchaseOrders?.length || 0}</Badge>
              </div>
              {editingProduct.customerPurchaseOrders && editingProduct.customerPurchaseOrders.length > 0 ? (
                <div className="space-y-2">
                  {editingProduct.customerPurchaseOrders.map((po) => (
                    <div key={po.id} className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-neutral-900">{po.name}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                          <span>{po.deal?.client?.name || 'No client'}</span>
                          <span>{po.deal?.orderNumber || `Deal #${po.dealId}`}</span>
                          <span>{formatFileSize(po.fileSize)}</span>
                          <span>{formatDate(po.uploadedAt)}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => customerPurchaseOrdersAPI.download(po)}
                        leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                      >
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No customer purchase orders are linked to this product yet.</p>
              )}
            </section>

            <section className="rounded-lg border border-neutral-200 bg-white p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                  <DocumentTextIcon className="h-5 w-5 text-primary-500" />
                  Documents
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowUploadForm(true);
                    setShowCostRangeForm(false);
                  }}
                  leftIcon={<DocumentArrowUpIcon className="h-4 w-4" />}
                >
                  Upload Document
                </Button>
              </div>

              {editingProduct.documents && editingProduct.documents.length > 0 ? (
                <div className="space-y-2">
                  {editingProduct.documents.map((doc) => (
                    <div key={doc.id} className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-neutral-900">{doc.fileName || doc.originalName}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                            <Badge variant="outline" size="sm">{doc.fileType}</Badge>
                            <span>{formatFileSize(doc.fileSize)}</span>
                            <span>{formatDate(doc.uploadedAt)}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                          <a
                            href={getDocumentUrl(doc.fileUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                          >
                            Open
                          </a>
                          <a
                            href={getDocumentUrl(doc.fileUrl)}
                            download={doc.fileName || doc.originalName}
                            className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50"
                          >
                            <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => beginEditDocument(doc)}
                            className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100"
                            title="Edit document"
                            aria-label={`Edit document ${doc.fileName || doc.originalName}`}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="inline-flex items-center rounded-md border border-error-200 bg-white px-2.5 py-1.5 text-error-600 transition-colors hover:bg-error-50"
                            title="Delete document"
                            aria-label={`Delete document ${doc.fileName || doc.originalName}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {editingDocumentId === doc.id && (
                        <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg border border-primary-200 bg-white p-3 sm:grid-cols-3">
                          <div className="sm:col-span-1">
                            <label className="block text-xs font-medium text-neutral-700">Document Type</label>
                            <select
                              value={documentEditForm.fileType}
                              onChange={(event) => setDocumentEditForm((current) => ({ ...current, fileType: event.target.value as DocumentType }))}
                              className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            >
                              <option value="COA">COA</option>
                              <option value="MSDS">MSDS</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-neutral-700">Display Name</label>
                            <input
                              aria-label="Document display name"
                              type="text"
                              value={documentEditForm.fileName}
                              onChange={(event) => setDocumentEditForm((current) => ({ ...current, fileName: event.target.value }))}
                              className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                            <Button variant="primary" size="sm" onClick={handleUpdateDocument} isLoading={savingDocument}>
                              Save Document
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingDocumentId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No documents uploaded yet.</p>
              )}

              {showUploadForm && (
                <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-neutral-900">Upload Document</h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Document Type</label>
                      <select
                        value={docType}
                        onChange={(event) => setDocType(event.target.value as DocumentType)}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="COA">COA</option>
                        <option value="MSDS">MSDS</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Display Name</label>
                      <input
                        type="text"
                        value={docName}
                        onChange={(event) => setDocName(event.target.value)}
                        placeholder="Defaults to file name"
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">File *</label>
                      <input
                        type="file"
                        onChange={(event) => setDocFile(event.target.files?.[0] || null)}
                        accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                        className="mt-1 block w-full text-sm text-neutral-500 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button variant="primary" size="sm" onClick={handleUploadDocument} isLoading={uploadingDoc} disabled={!docFile}>
                      Upload
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setShowUploadForm(false);
                      setDocFile(null);
                      setDocName('');
                      setDocType('COA');
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CatalogueView;
